import {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import {
    borderRadius,
    borderWidth,
    colors,
    contentWidth,
    fontSize,
    fontWeight,
    gradients,
    letterSpacing,
    lineHeight,
    overlay,
    spacing,
    transition
} from '@/styles/tokens';

/** A cell is either copy, or a yes/no marker. */
export type MatrixCell = string | {type: 'check' | 'cross'};

export interface MatrixRow {
    /** Row header — the criterion being compared. */
    criteria: string;
    /** One cell per column, in `columns` order. */
    cells: MatrixCell[];
}

export interface MatrixTableProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    /** Label above the row-header column, e.g. "Decision Criteria". */
    criteriaLabel: string;
    /** Column headings, left to right. */
    columns: string[];
    rows: MatrixRow[];
    /** `white` (default) has dark heading copy for light sections; `blue` flips the
     *  heading and subheading to light copy for a `bg-deep-blue` wrapper. The table
     *  itself stays white either way. */
    variant?: 'white' | 'blue';
}

// On the navy ground both the heading and the subheading are solid white.
const blueTextSx = {color: colors.text.inverse} as const;

const tableWrapSx = {
    border: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius['3xl'],
    bgcolor: colors.background.default,
    overflow: 'hidden'
} as const;

// Wide content scrolls inside its own container so the page body never scrolls sideways.
const scrollWrapSx = {overflowX: 'auto'} as const;

const tableSx = {
    width: '100%',
    minWidth: contentWidth.tableMin,
    borderCollapse: 'collapse' as const
} as const;

// Navy header on white body rows, as in the site's other comparison tables
// (PlatformComparison, WhiteLabelComparison) — not a tinted header with zebra rows.
const headerRowSx = {background: gradients.navyCardDeep} as const;

// `blue` variant — a frosted-glass table on the navy section. The white fills are low
// alpha so the navy shows through, which means the copy must be white: dark text on a
// faint white wash over navy fails contrast. Rows alternate between two faint fills.
// Solid white header over the glass rows, so the column labels anchor the table; on
// white the label copy goes dark. The criteria label drops its small-caps eyebrow style
// here and matches the column labels, so the whole header row reads as one weight.
const glassHeaderRowSx = {
    bgcolor: colors.background.default,
    '& > th': {color: colors.text.primary},
    '& > th:first-of-type': {
        fontSize: fontSize[16],
        letterSpacing: 'normal',
        textTransform: 'none',
        lineHeight: lineHeight[24]
    }
} as const;

const glassWrapSx = {
    ...tableWrapSx,
    bgcolor: 'transparent',
    borderColor: overlay.whiteBorder,
    backdropFilter: 'blur(8px)'
} as const;

const cellBaseSx = {
    px: {xs: spacing[16], md: spacing[24]},
    py: spacing[16],
    textAlign: 'left' as const,
    verticalAlign: 'middle' as const
} as const;

const criteriaHeaderSx = {
    ...cellBaseSx,
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    color: overlay.whiteLabel,
    letterSpacing: letterSpacing.eyebrow,
    textTransform: 'uppercase' as const,
    lineHeight: lineHeight[20]
} as const;

const columnHeaderSx = {
    ...cellBaseSx,
    fontSize: fontSize[16],
    fontWeight: fontWeight.bold,
    color: colors.text.inverse,
    lineHeight: lineHeight[24]
} as const;

// Rules separate the rows, with alternate rows tinted so the eye can track a long
// criterion across four columns. The last rule is dropped so nothing cuts across the
// container's rounded bottom edge.
const bodyRowSx = {
    '&:nth-of-type(even)': {bgcolor: colors.background.debutHighlight},
    // Hover tints the whole row, striped or not, so reading one criterion across four
    // columns is easier to follow with the cursor. It wins over the stripe by being
    // declared after it.
    transition: `background-color ${transition.medium}`,
    '&:hover, &:nth-of-type(even):hover': {bgcolor: colors.background.missionCard},
    '& > *': {borderBottom: `${borderWidth.thin} solid ${colors.neutral.borderLight}`},
    '&:last-of-type > *': {borderBottom: 'none'}
} as const;

// Element-level selectors outrank the cells' own class colours, so every cell flips to
// white without a second set of cell styles.
const glassBodyRowSx = {
    ...bodyRowSx,
    bgcolor: overlay.whiteHint,
    '&:nth-of-type(even)': {bgcolor: overlay.whiteSubtle},
    '&:hover, &:nth-of-type(even):hover': {bgcolor: overlay.whiteFaint},
    '& > th, & > td': {color: colors.text.inverse},
    '& > *': {borderBottom: `${borderWidth.thin} solid ${overlay.whiteMid}`},
    '&:last-of-type > *': {borderBottom: 'none'}
} as const;

const criteriaCellSx = {
    ...cellBaseSx,
    fontSize: fontSize[15],
    fontWeight: fontWeight.semibold,
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

const valueCellSx = {
    ...cellBaseSx,
    fontSize: fontSize[15],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

const checkIconSx = {color: colors.accent.emeraldDark, fontSize: fontSize[20]} as const;
const crossIconSx = {color: colors.status.error, fontSize: fontSize[20]} as const;

const isMarker = (cell: MatrixCell): cell is {type: 'check' | 'cross'} => typeof cell !== 'string';

/**
 * MatrixTable — a criteria × options comparison grid: one row header per
 * criterion, one column per option, cells holding either copy or a yes/no marker.
 *
 * A real `<table>` with `<th scope>` on both axes, so a screen reader announces
 * which criterion and which column a cell belongs to. Markers carry
 * `titleAccess`, never colour alone.
 *
 * Distinct from `ComparisonTable`, which is the "us vs them" variant: one
 * highlighted column, a ★ Recommended badge, and four fixed column keys.
 */
const MatrixTable = ({eyebrow, heading, subheading, criteriaLabel, columns, rows, variant = 'white'}: MatrixTableProps) => (
    <MUIContainer>
        <SectionHeader
            eyebrow={eyebrow}
            heading={heading}
            subheading={subheading}
            headingSx={variant === 'blue' ? blueTextSx : undefined}
            subheadingSx={variant === 'blue' ? blueTextSx : undefined}
        />

        <Box sx={variant === 'blue' ? glassWrapSx : tableWrapSx}>
            <Box sx={scrollWrapSx}>
                <Box component="table" sx={tableSx}>
                    <Box component="thead">
                        <Box component="tr" sx={variant === 'blue' ? glassHeaderRowSx : headerRowSx}>
                            <Box component="th" scope="col" className="font-headers" sx={criteriaHeaderSx}>
                                {criteriaLabel}
                            </Box>
                            {columns.map((column) => (
                                <Box key={column} component="th" scope="col" className="font-headers" sx={columnHeaderSx}>
                                    {column}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {rows.map((row) => (
                            <Box component="tr" key={row.criteria} sx={variant === 'blue' ? glassBodyRowSx : bodyRowSx}>
                                <Box component="th" scope="row" className="font-headers" sx={criteriaCellSx}>
                                    {row.criteria}
                                </Box>
                                {row.cells.map((cell, index) => (
                                    <Box component="td" key={columns[index] ?? index} sx={valueCellSx}>
                                        {isMarker(cell) ? (
                                            cell.type === 'check' ? (
                                                <CheckRoundedIcon sx={checkIconSx} titleAccess="Included" />
                                            ) : (
                                                <CloseRoundedIcon sx={crossIconSx} titleAccess="Not included" />
                                            )
                                        ) : (
                                            <Typography component="span" className="font-regular-text" sx={{color: 'inherit'}}>
                                                {cell}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    </MUIContainer>
);

export default memo(MatrixTable);
